<template>
  <div>
    <t-skeleton v-if="fetching" />
    <div
      v-else-if="!fetching && messageDetails"
      class="tw-flex tw-gap-4 tw-flex-col lg:tw-flex-row"
    >
      <div
        class="tw-border tw-border-primary-grey-100 tw-rounded-md tw-p-4 tw-flex-grow tw-min-w-[66%] tw-bg-white"
      >
        <section>
          <div class="tw-font-bold tw-text-xl tw-mb-4">
            {{ $t("subject") }} {{ messageDetails.subject }}
          </div>
          <IncidentStatusAndFlagReason />
          <IncidentEssentials :message-details="messageDetails" />
        </section>
        <section
          v-if="messageDetails?.attachments?.length"
          class="tw-mb-4 tw-flex tw-flex-wrap tw-gap-4"
        >
          <IncidentAttachments :attachments="messageDetails.attachments" />
        </section>
        <section
          v-if="messageDetails.body"
          class="tw-relative tw-bg-[#F9F9F9] tw-flex tw-justify-center tw-p-4 tw-border tw-border-primary-grey-100 tw-rounded-md"
        >
          <authorisation-wrapper
            :resource="EMAIL_SECURITY"
            :action="WRITE"
            v-slot="{ isAllowed }"
          >
            <iframe
              v-if="isAllowed"
              :srcdoc="messageDetails?.body.content"
              class="tw-min-h-[70vh] tw-w-full tw-max-w-screen-sm"
              data-clarity-mask="true"
            ></iframe>
            <div v-else class="tw-w-full">
              <div>
                <div
                  class="tw-absolute tw-top-1/2 tw-left-1/2 tw--translate-x-1/2 tw--translate-y-1/2 tw-z-10"
                >
                  <cl-callout
                    :close-text="$t('close')"
                    :test-id="'incident-details-readonly-callout'"
                    variant="info"
                  >
                    <template #title>
                      {{ $t("restricted-view-title") }}
                    </template>
                    <template #message>
                      {{ $t("restricted-view-banner") }}
                    </template>
                  </cl-callout>
                </div>
                <div class="tw-flex tw-justify-center">
                  <iframe
                    :srcdoc="messageDetails?.body.content"
                    class="tw-min-h-[70vh] tw-w-full tw-max-w-screen-sm tw-blur-sm"
                  ></iframe>
                </div>
              </div>
            </div>
          </authorisation-wrapper>
        </section>
      </div>
      <div>
        <div class="tw-flex tw-flex-col tw-gap-4">
          <section
            v-if="reasonsForDetection?.length"
            class="tw-border tw-border-primary-grey-100 tw-rounded-md tw-bg-white tw-p-4"
          >
            <cl-heading :size="'h6'">{{
              $t("reasons-for-detection")
            }}</cl-heading>
            <IncidentReasonsForDetections
              :reasons-for-detection="reasonsForDetection"
            />
          </section>
          <section
            v-if="emailAnalysis?.length > 0"
            class="tw-border tw-border-primary-grey-100 tw-rounded-md tw-bg-white tw-p-4"
          >
            <div
              class="tw-mb-4 tw-text-lg tw-font-semibold tw-text-primary-grey-700"
            >
              {{ $t("email-analysis") }}
            </div>
            <IncidentEmailAnalysis :email-analysis="emailAnalysis" />
          </section>
          <section
            v-if="urlAnalysis?.length > 0"
            class="tw-border tw-border-primary-grey-100 tw-rounded-md tw-bg-white tw-p-4"
          >
            <div
              class="tw-mb-4 tw-text-lg tw-font-semibold tw-text-primary-grey-700"
            >
              {{ $t("url-analysis") }}
            </div>
            <IncidentUrlAnalysis :url-analysis="urlAnalysis" />
          </section>
          <section
            v-if="senderAnalysis?.length > 0 || domainInformation"
            class="tw-border tw-border-primary-grey-100 tw-rounded-md tw-bg-white tw-p-4"
          >
            <div
              class="tw-flex tw-w-full tw-flex-col tw-justify-between tw-rounded-sm tw-bg-white tw-p-6"
            >
              <div v-if="senderAnalysis?.length > 0">
                <div
                  class="tw-mb-4 tw-text-lg tw-font-semibold tw-text-primary-grey-700"
                >
                  {{ $t("sender-details") }}
                </div>
                <IncidentSenderAnalysis :sender-analysis="senderAnalysis" />
              </div>
              <div v-if="domainInformation">
                <IncidentDomain :domain-information="domainInformation" />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
    <IncidentEmpty v-else />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

import { useGlobalOrganisationIDBasedOnRoute } from "@/composables/useGlobalOrganisationIDBasedOnRoute";
import { useIncidents } from "@/modules/PhishTitan/views/Common/Incidents/useIncidents.store";

import TSkeleton from "@/components/TSkeleton";
import {
  IncidentEmpty,
  IncidentAttachments,
  IncidentDomain,
  IncidentEmailAnalysis,
  IncidentEssentials,
  IncidentReasonsForDetections,
  IncidentSenderAnalysis,
  IncidentStatusAndFlagReason,
  IncidentUrlAnalysis,
} from "@/modules/PhishTitan/views/Common/Incidents/Common";
import { useIsMspRoute } from "@/composables/useIsMspRoute";
import { CUSTOMER, MSP } from "@/constants";
import { EMAIL_SECURITY, WRITE } from "@/authorisation";
import { AuthorisationWrapper } from "@/authorisation";

const { globalOrganisationId } = useGlobalOrganisationIDBasedOnRoute();

const { incidentDetails, getIncident } = useIncidents();

const route = useRoute();
const ingressId = route.params.ingressId as string;

const fetching = ref(true);

const messageDetails = computed(() => incidentDetails.value?.message || null);
const emailAnalysis = computed(
  () => incidentDetails.value?.email_analysis || [],
);
const urlAnalysis = computed(() => incidentDetails.value?.url_analysis || []);
const reasonsForDetection = computed(
  () => incidentDetails.value?.details?.antispam?.messages,
);
const senderAnalysis = computed(
  () => incidentDetails.value?.sender_analysis || [],
);
const domainInformation = computed(
  () => incidentDetails.value?.domain_information || null,
);
const { isMspRoute } = useIsMspRoute();

const getIncidentDetails = async () => {
  if (!globalOrganisationId.value) return;

  try {
    fetching.value = true;

    await getIncident({
      globalOrganisationID: globalOrganisationId.value,
      ingressID: ingressId,
      params: {
        organisationType: isMspRoute.value ? MSP : CUSTOMER,
      },
    });
  } finally {
    fetching.value = false;
  }
};

getIncidentDetails();
</script>
